/**
 * Harici yedeğin takvimi (ADR 0041 §4, §5): veritabanı görüntüsünün zamanı ve paket kesiminin
 * zamanı. Saf fonksiyonlar: saat ve gemi saati farkı parametreyle gelir (CLAUDE.md
 * "packages/domain").
 */

/** Görüntünün nedeni (ADR 0041 §5 tablosu). */
export const SNAPSHOT_REASONS = [
  'daily',
  'activation',
  'pre_migration',
  'manual',
  'restored_hourly',
] as const;
export type SnapshotReason = (typeof SNAPSHOT_REASONS)[number];

/** Günlük görüntünün gemi yerel saati: bundan sonraki ilk tur (ADR 0041 §5). */
export const DAILY_SNAPSHOT_LOCAL_HOUR = 2;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/** Son başarılı günlük görüntüden bu kadar geçtiyse yerel saat beklenmez. */
export const DAILY_SNAPSHOT_MAX_AGE_MS = 24 * HOUR;
/**
 * Yerel gün tekrar etse de (tarih çizgisini doğuya geçiş) iki günlük görüntü arası bunu geçmez.
 */
export const DAILY_SNAPSHOT_HARD_MAX_AGE_MS = 48 * HOUR;
/** Geri yüklenmiş, etkinleştirme bekleyen sunucuda görüntü aralığı (ADR 0041 §12). */
export const RESTORED_SNAPSHOT_INTERVAL_MS = HOUR;

/** Gemi sunucusunun durumu: görüntü takvimini belirler. */
export type ServerBackupState = 'active' | 'restored_pending' | 'not_activated' | 'superseded';

export interface SnapshotDueInput {
  now: Date;
  /** Gemi saati farkı, dakika (şimdiki). */
  tzOffsetMin: number;
  state: ServerBackupState;
  /** Son başarılı günlük görüntü ve alındığı andaki gemi saati farkı. */
  lastDaily: { at: string; tzOffsetMin: number } | null;
  /** Herhangi bir nedenle alınan son başarılı görüntü (saatlik görüntü için). */
  lastSnapshotAt: string | null;
}

function localDate(ms: number, tzOffsetMin: number): string {
  return new Date(ms + tzOffsetMin * MINUTE).toISOString().slice(0, 10);
}

function localHour(ms: number, tzOffsetMin: number): number {
  return new Date(ms + tzOffsetMin * MINUTE).getUTCHours();
}

/**
 * Sıradaki görüntü zamanı geldiyse nedeni, gelmediyse `null`:
 * - **etkin sunucu:** gemi yerel saatiyle 02:00'den sonraki ilk tur; son başarılı günlük
 *   görüntüden 24 saat geçtiyse yerel saat beklenmez [NR496 4.3.2b; mimari belge §10.7]. Aynı yerel
 *   günde iki günlük görüntü alınmaz; yerel gün, son görüntünün kendi saat farkıyla hesaplanır, bu
 *   yüzden saat dilimi değişince görüntü atlanmaz. Yine de iki günlük görüntü arası 48 saati
 *   geçmez (tarih çizgisinde yerel gün tekrar eder);
 * - **etkinleştirme bekleyen sunucu:** saatlik (`restored_hourly`; paket dosyası yoktur, ADR 0041
 *   §12);
 * - etkinleştirilmemiş ya da yerine başkası geçmiş sunucu kendiliğinden görüntü almaz (elle ve
 *   migrate öncesi görüntü ayrı yoldur).
 */
export function snapshotDue(input: SnapshotDueInput): 'daily' | 'restored_hourly' | null {
  const now = input.now.getTime();
  if (input.state === 'restored_pending') {
    if (input.lastSnapshotAt === null) return 'restored_hourly';
    return now - Date.parse(input.lastSnapshotAt) >= RESTORED_SNAPSHOT_INTERVAL_MS
      ? 'restored_hourly'
      : null;
  }
  if (input.state !== 'active') return null;
  if (input.lastDaily === null) return 'daily';
  const last = Date.parse(input.lastDaily.at);
  const age = now - last;
  if (age >= DAILY_SNAPSHOT_HARD_MAX_AGE_MS) return 'daily';
  if (localDate(now, input.tzOffsetMin) === localDate(last, input.lastDaily.tzOffsetMin)) {
    return null;
  }
  if (localHour(now, input.tzOffsetMin) >= DAILY_SNAPSHOT_LOCAL_HOUR) return 'daily';
  return age >= DAILY_SNAPSHOT_MAX_AGE_MS ? 'daily' : null;
}

export interface CutDueInput {
  /** Kesilmemiş ilk yazmanın zamanı (ms); bekleyen yazma yoksa `null`. */
  firstPendingAt: number | null;
  /** Son yazmanın zamanı (ms). */
  lastWriteAt: number | null;
  /** Son yazmadan bu kadar sonra (varsayılan 15 sn; `cutDebounceMs`). */
  debounceMs: number;
  /** İlk bekleyen yazmadan en geç bu kadar sonra (varsayılan 60 sn; `maxCutDelayMs`). */
  maxDelayMs: number;
}

/**
 * Paket kesim zamanı (ms; ADR 0041 §4): son yazmadan `debounceMs` sonra, ama ilk bekleyen
 * yazmadan en geç `maxDelayMs` sonra. Çevrimdışı kopya her değişiklikte güncellenir
 * [MEPC.312 4.4.2–4.4.3, en iyi uygulama; mimari belge §6.5]. Bekleyen yazma yoksa `null`.
 */
export function cutDueAt(input: CutDueInput): number | null {
  if (input.firstPendingAt === null) return null;
  const last = input.lastWriteAt ?? input.firstPendingAt;
  return Math.min(last + input.debounceMs, input.firstPendingAt + input.maxDelayMs);
}
